import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { trpc } from '../lib/trpc';
import type { Event } from '@wineryxplorer/shared';
import { VenueEventRow } from './venueEvents/VenueEventRow';
import { Button } from './ui/Button';
import { FormSection } from './ui/FormSection';

/** Which status list the venue-events manager is showing. */
type EventTab = 'published' | 'draft' | 'past';

const TABS: { id: EventTab; label: string }[] = [
  { id: 'published', label: 'Published' },
  { id: 'draft', label: 'Draft' },
  { id: 'past', label: 'Past' },
];

const EMPTY_STATE_COPY: Record<EventTab, string> = {
  published:
    'Nothing here is visible to the public yet. Create an event, then set its status to Published on its edit page.',
  draft: 'Events you are still working on will appear here.',
  past: 'Cancelled and completed events will appear here.',
};

interface VenueEventsManagementProps {
  venueId: string;
}

export const VenueEventsManagement = ({ venueId }: VenueEventsManagementProps) => {
  const navigate = useNavigate();
  const [deletingEventId, setDeletingEventId] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<EventTab>('published');
  const utils = trpc.useContext();

  // Fetch events filtered by status on the server side for better performance
  const statusFilter =
    activeTab === 'published'
      ? ('published' as const)
      : activeTab === 'draft'
        ? ('draft' as const)
        : ('cancelled' as const);

  const { data, isPending, error } = trpc.event.listByVenue.useQuery({
    venueId,
    status: statusFilter,
  });

  const deleteEvent = trpc.event.delete.useMutation({
    onSuccess: () => {
      // Invalidate and refetch events for current tab
      utils.event.listByVenue.invalidate({ venueId, status: statusFilter });
      setDeletingEventId(null);
    },
    onError: (error) => {
      alert(`Error deleting event: ${error.message}`);
      setDeletingEventId(null);
    },
  });

  const handleCreateEvent = () => {
    navigate(`/events/new?venueId=${venueId}`);
  };

  const handleEditEvent = (eventId: string) => {
    // `from=manage` tells the edit page this flow started at the hub, so it
    // returns here on save or cancel instead of the event's own page.
    navigate(`/events/${eventId}/edit?from=manage`);
  };

  const handleDeleteEvent = (eventId: string) => {
    if (confirm('Are you sure you want to delete this event? This action cannot be undone.')) {
      setDeletingEventId(eventId);
      deleteEvent.mutate({ id: eventId });
    }
  };

  if (isPending) {
    return (
      <div className="py-8">
        <p className="text-gray-600">Loading events...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="py-8">
        <p className="text-error">Error loading events: {error.message}</p>
      </div>
    );
  }

  // Cast events to proper type - events are already filtered by status on the server
  const sortedEvents = (data?.items ?? []) as Event[];

  // Get total count for display
  const totalCount = data?.total ?? 0;

  return (
    <div className="space-y-6">
      {/* Header with Create Button */}
      <FormSection
        title="Your Events"
        titleAs="h3"
        // Leads its card on the venue management hub, so it draws no rule.
        divider={false}
        className="mt-0"
        description={`${totalCount} ${activeTab} ${totalCount === 1 ? 'event' : 'events'}`}
        actions={<Button onClick={handleCreateEvent}>Create Event</Button>}
      />

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8" aria-label="Event status tabs">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              aria-current={activeTab === tab.id ? 'page' : undefined}
              className={`
                whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm
                ${
                  activeTab === tab.id
                    ? 'border-primary-700 text-primary-800'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }
              `}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Events List */}
      {sortedEvents.length > 0 ? (
        <div className="overflow-hidden rounded-lg border border-gray-200">
          {sortedEvents.map((event) => (
            <VenueEventRow
              key={event.id}
              event={event}
              isDeleting={deletingEventId === event.id}
              onEdit={handleEditEvent}
              onDelete={handleDeleteEvent}
            />
          ))}
        </div>
      ) : (
        <div className="rounded-lg border border-gray-200 bg-gray-100 px-6 py-12 text-center">
          <h3 className="font-serif text-lg text-gray-900">No {activeTab} events</h3>
          <p className="mx-auto mt-2 max-w-md text-sm text-gray-600">
            {EMPTY_STATE_COPY[activeTab]}
          </p>
          {activeTab === 'published' && (
            <div className="mt-6">
              <Button onClick={handleCreateEvent}>Create Your First Event</Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
